import { CLASS_NAME } from 'constants/class-names'
import { ATTR_NAME } from 'constants/attr-name'
import {
    HtmlAttr,
    EventType,
    Position,
    PopoverEvent,
    KeyType
} from 'constants/enums'
import { hasTouch } from 'utils/system/device'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { strToBool } from 'utils/string'
import { PopoverStore } from './store'

type BubbleAttrs = {
    triggerPositionX: number
    triggerPositionY: number
    triggerWidth: number
    triggerHeight: number
    contentWidth: number
    contentHeight: number
    tailWidth: number
    containerPositionX: number
    containerWidth: number
}

type BubbleStyles = {
    style: string
    styleTail: string
}

const { popover: className } = CLASS_NAME
const { popover: attrName } = ATTR_NAME.main

export class PopoverComponent extends ComponentBase {
    private position: Position = Position.BOTTOM
    private visibility = false
    private _container: string
    private containerOffset: number
    private _sticky: string
    private tailOffset: number
    private tailPosition: string
    private noStyle: boolean

    // eslint-disable-next-line no-magic-numbers
    private readonly offset = 3

    /**
     * Get the content of the popover.
     * @return Nullable<HTMLDivElement>
     */
    private get content(): Nullable<HTMLDivElement> {
        return this.getElement(`.${className.content}`, this.el)
    }

    /**
     * Get the trigger of the popover.
     * @return Nullable<HTMLButtonElement>
     */
    private get trigger(): Nullable<HTMLButtonElement> {
        return this.getElement<HTMLButtonElement>(
            `.${className.trigger}`,
            this.el
        )
    }

    /**
     * Get the event of the popover.
     * @return Nullable<HTMLButtonElement>
     */
    private get event(): Nullable<HTMLButtonElement> {
        return this.getElement<HTMLButtonElement>(
            `.${className.event}`,
            this.el
        )
    }

    /**
     * Get the close button of the popover.
     * @return Nullable<HTMLButtonElement>
     */
    private get closeButton(): Nullable<HTMLButtonElement> {
        return this.getElement<HTMLButtonElement>(
            `.${className.close}`,
            this.el
        )
    }

    /**
     * Get the bubble tail of the popover.
     * @return Nullable<HTMLDivElement>
     */
    private get bubbleTail(): Nullable<HTMLDivElement> {
        return this.getElement<HTMLDivElement>(`.${className.tail}`, this.el)
    }

    /**
     * Get the container.
     * @return Nullable<HTMLElement>
     */
    private get container(): Nullable<HTMLElement> {
        if (this._container) {
            return this.getElement(this._container)
        }
        return undefined
    }

    /**
     * Get the sticky container.
     * @return Nullable<HTMLElement>
     */
    private get sticky(): Nullable<HTMLElement> {
        if (this._sticky) {
            return this.getElement(this._sticky)
        }
        return undefined
    }

    /**
     * Initializes the popover.
     * @param {HTMLDivElement} el
     */
    public constructor(private el: HTMLDivElement) {
        super()

        if (!this.trigger) {
            return
        }

        PopoverStore.getInstance().add(this)

        this.position = (el.getAttribute(attrName.position) ||
            this.position) as Position

        this._container = el.getAttribute(attrName.container) || ''

        this.containerOffset = parseFloat(
            el.getAttribute(attrName.containerOffset) || '0'
        )

        this.tailOffset = parseFloat(
            el.getAttribute(attrName.tailOffset) || '0'
        )

        this.tailPosition = el.getAttribute(attrName.tailPosition) || ''

        this._sticky = el.getAttribute(attrName.sticky) || ''

        this.noStyle = strToBool(el.getAttribute(attrName.noStyle), false)

        const clickToAppear = el.getAttribute(attrName.clickToAppear) === 'True'

        el.addEventListener(PopoverEvent.OPEN, () => {
            this.visibility = true
            this.togglePopover()
        })
        el.addEventListener(PopoverEvent.CLOSE, () => {
            this.visibility = false
            this.togglePopover()
        })

        // Non-mobile browser only
        if (!hasTouch() && !clickToAppear) {
            el.addEventListener(EventType.MOUSE_OVER, () => {
                this.visibility = true
                this.togglePopover()
            })
            el.addEventListener(EventType.MOUSE_LEAVE, () => {
                this.visibility = false
                this.togglePopover()
            })
        } else {
            this.observePopover(el)
        }

        this.trigger.addEventListener(EventType.CLICK, (e) => {
            e.preventDefault()

            PopoverStore.getInstance().close()
            this.visibility = true
            // click to appear will be ignored if the event is activated by class `popover_event`
            if (!this.event) {
                this.togglePopover()
            }
        })

        if (!clickToAppear) {
            this.trigger.addEventListener(EventType.FOCUS, () => {
                this.visibility = true
                this.togglePopover()
            })
            this.trigger.addEventListener(EventType.BLUR, () => {
                this.visibility = false
                this.togglePopover()
            })
        }

        if (this.closeButton) {
            this.closeButton.addEventListener(EventType.CLICK, (e) => {
                e.preventDefault()
                this.visibility = false
                this.togglePopover()
            })
            document.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
                if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
                    this.visibility = false
                    this.togglePopover()
                }
            })
        }
    }

    /**
     * Observe Popover element and close the popover
     * if the target element is not in intersects area
     * @param {Element} el
     */
    public observePopover(el: Element): void {
        if (!el) {
            return
        }
        const intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        this.visibility = false
                        this.togglePopover()
                    }
                })
            },
            {
                rootMargin: '0px',
                threshold: 1.0
            }
        )
        intersectionObserver.observe(el)
    }

    /**
     * Close the popover
     */
    public close(): void {
        if (!this.content) {
            return
        }

        this.content.hidden = true
    }

    /**
     * Show or hide the popover
     */
    private togglePopover(): void {
        if (!this.content || !this.trigger || !this.bubbleTail) {
            return
        }

        // Content visibility
        if (this.visibility) {
            this.content.hidden = false
        } else {
            this.close()
            return
        }

        if (this.noStyle) {
            return
        }

        // Bubble position
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const triggerRect = this.trigger.getBoundingClientRect()
        const bubbleAttrs = {
            triggerPositionY: triggerRect.top + scrollTop,
            triggerPositionX: triggerRect.left + scrollLeft,
            triggerWidth: this.trigger.offsetWidth,
            triggerHeight: this.trigger.offsetHeight,
            contentWidth: this.content.offsetWidth,
            contentHeight: this.content.offsetHeight,
            tailWidth: this.getHalf(
                // eslint-disable-next-line no-magic-numbers
                Math.sqrt(Math.pow(this.bubbleTail.offsetWidth, 2) * 2)
            ),
            containerPositionX: 0,
            containerWidth: 0
        }
        if (this.container) {
            const containerRect = this.container.getBoundingClientRect()
            bubbleAttrs.containerWidth =
                this.container.offsetWidth - this.containerOffset
            bubbleAttrs.containerPositionX = containerRect.left + scrollLeft
        }
        if (this.sticky) {
            const stickyRect = this.sticky.getBoundingClientRect()
            bubbleAttrs.triggerPositionY = triggerRect.top - stickyRect.top
        }
        const { style, styleTail } = this.getStyles(bubbleAttrs)
        this.content.setAttribute(HtmlAttr.STYLE, style)
        this.bubbleTail.setAttribute(HtmlAttr.STYLE, styleTail)
    }

    /**
     * Calculate the style of the popover.
     * @param {BubbleAttrs} bubbleAttrs
     * @return BubbleStyles
     */
    private getStyles(bubbleAttrs: BubbleAttrs): BubbleStyles {
        const {
            triggerPositionX,
            triggerPositionY,
            triggerWidth,
            triggerHeight,
            tailWidth,
            containerWidth,
            containerPositionX
        } = bubbleAttrs
        let { contentWidth, contentHeight } = bubbleAttrs

        let style = '',
            styleTail = '',
            left = 0,
            tailLeft = 0,
            tailTop = 0,
            top = 0,
            tailShift = 0

        if (
            this.container &&
            this.content &&
            (this.position === Position.TOP ||
                this.position === Position.BOTTOM)
        ) {
            this.content.setAttribute(
                HtmlAttr.STYLE,
                `width:${containerWidth}px`
            )
            style += `width:${containerWidth}px;`
            contentWidth = containerWidth
            contentHeight = this.content.offsetHeight
        }

        // Common positions
        switch (this.position) {
            case Position.LEFT:
            case Position.RIGHT:
                top =
                    triggerPositionY +
                    this.getHalf(triggerHeight) -
                    this.getHalf(contentHeight)
                tailTop = this.getHalf(contentHeight) + this.offset - tailWidth
                break
            default:
                left =
                    triggerPositionX +
                    this.getHalf(triggerWidth) -
                    this.getHalf(contentWidth)
                if (this.container) {
                    const newLeft =
                        containerPositionX + this.getHalf(this.containerOffset)
                    tailShift = left - newLeft
                    left = newLeft
                }

                if (this.tailPosition === 'left') {
                    tailShift = -this.getHalf(this.tailOffset)
                    left = left + this.getHalf(this.tailOffset)
                } else if (this.tailPosition === 'right') {
                    tailShift = this.getHalf(this.tailOffset)
                    left = left - this.getHalf(this.tailOffset)
                }

                tailLeft =
                    this.getHalf(contentWidth) -
                    tailWidth +
                    this.offset +
                    tailShift
                break
        }

        switch (this.position) {
            case Position.LEFT:
                left = triggerPositionX - contentWidth - tailWidth
                tailLeft = contentWidth - tailWidth + this.offset
                styleTail += 'transform:rotate(135deg);'
                break
            case Position.RIGHT:
                left = triggerPositionX + triggerWidth + tailWidth
                tailLeft = tailWidth * -1 + this.offset
                styleTail += 'transform:rotate(-45deg);'
                break
            case Position.TOP:
                top =
                    triggerPositionY - contentHeight - this.offset * this.offset
                // eslint-disable-next-line no-magic-numbers
                tailTop = contentHeight - tailWidth + 2
                styleTail += 'transform:rotate(225deg);'
                break
            case Position.BOTTOM:
            default:
                top =
                    triggerPositionY +
                    triggerHeight +
                    tailWidth -
                    // eslint-disable-next-line no-magic-numbers
                    this.offset * 2
                tailTop = tailWidth * -1 + this.offset
                styleTail += 'transform:rotate(45deg);'
                break
        }

        style += `left:${left}px;top:${top}px`
        styleTail += `left:${tailLeft}px;top:${tailTop}px`
        return { style, styleTail }
    }

    /**
     * Get the half of given value
     * @param {number} value
     * @return number
     */
    private getHalf(value: number): number {
        // eslint-disable-next-line no-magic-numbers
        return value / 2
    }
}
